<template>
  <div
    class="r3-datatable-container w-full h-full min-h-0 overflow-hidden flex flex-column"
    :class="$attrs.class"
    :style="$attrs.style as any"
  >
    <DataTable
      v-bind="$attrs"
      :responsive-layout="props.responsiveLayout"
      :value="props.tableValues"
      :data-key="String(props.keyField)"
      :table-style="props.tableStyle"
      :scrollable="props.scrollable"
      :scroll-height="resolvedScrollHeight"
      :virtual-scroller-options="resolvedVirtualScrollerOptions"
      :show-gridlines="props.gridLines"
      :selectionMode="props.selectionMode ?? undefined"
      v-model:selection="selectionModel"
      class="r3-datatable w-full h-full flex-1 flex flex-column"
    >
      <Column
        v-if="props.selectionMode === 'multiple'"
        selectionMode="multiple"
        headerStyle="width: 3rem"
      />
      <Column
        v-for="(field, index) in normalizedFields"
        :key="field"
        :field="field"
        :header="props.fieldsHeader?.[index] ?? field"
        :body-style="columnCellStyle(index)"
        :header-style="columnCellStyle(index)"
        :body-class="columnAlignClass(index)"
        :header-class="columnAlignClass(index)"
      >
        <!--
          Só o slot de body (não o de header): o HeaderCell do PrimeVue
          renderiza slot #header E a prop :header juntos, o que duplicava
          o título (EAN/DUM14 / Qtd).
        -->
        <template #body="{ data }">
          <!--
            Flex (não text-align): o valor da célula fica no centro/direita/esquerda
            mesmo com o tema forçando text-align:left na td.
          -->
          <div class="r3-datatable-cell" :style="columnContentStyle(index)">
            {{ data?.[field] }}
          </div>
        </template>
      </Column>
    </DataTable>
  </div>
</template>

<script setup lang="ts" generic="T extends object">
import Column from "primevue/column";
import DataTable from "primevue/datatable";
import type { VirtualScrollerProps } from "primevue/virtualscroller";
import { computed } from "vue";

/** Alinhamento horizontal por coluna (cabeçalho e células). */
export type R3DataTableColumnAlign = "left" | "center" | "right";

defineOptions({
  inheritAttrs: false
});

const props = withDefaults(defineProps<{
  tableValues: T[];
  keyField: keyof T | string;
  responsiveLayout?: "scroll" | "stack";
  gridLines?: boolean;
  tableStyle?: Partial<CSSStyleDeclaration>;
  scrollHeight?: string;
  scrollable?: boolean;
  virtualScrollerOptions?: VirtualScrollerProps | null;
  fields?: Array<keyof T | string>;
  fieldsHeader?: string[];
  /**
   * Alinhamento por coluna, na mesma ordem de `fields`.
   * Ex.: `['center', 'right']` centraliza a 1ª e alinha a 2ª à direita.
   * Colunas sem valor usam `left`.
   */
  fieldsAlign?: Array<R3DataTableColumnAlign | undefined | null>;
  selectionMode?: "single" | "multiple" | null;
}>(), {
  responsiveLayout: "scroll",
  gridLines: false,
  scrollable: true,
  scrollHeight: "flex",
  selectionMode: null,
  virtualScrollerOptions: () => ({
    itemSize: 50
  })
});

const selectionModel = defineModel("selection");

const normalizedFields = computed(() => props.fields?.map((field) => String(field)) ?? []);
const resolvedScrollHeight = computed(() => props.scrollHeight ?? "100%");
const resolvedVirtualScrollerOptions = computed(() => {
  if (props.virtualScrollerOptions === null) return undefined;
  return props.virtualScrollerOptions;
});

function columnAlign(index: number): R3DataTableColumnAlign {
  return props.fieldsAlign?.[index] ?? "left";
}

function columnAlignClass(index: number): string {
  const align = columnAlign(index);
  if (align === "center") return "r3-align-center";
  if (align === "right") return "r3-align-right";
  return "r3-align-left";
}

/**
 * String com !important: o tema blue/saga fixa text-align:left em td/th
 * e o objeto style do Vue não permite !important.
 */
function columnCellStyle(index: number): string {
  const align = columnAlign(index);
  return `white-space: nowrap; text-align: ${align} !important;`;
}

/** Estilo do conteúdo (valor) da célula — flex centra a informação de fato. */
function columnContentStyle(index: number): Record<string, string> {
  const align = columnAlign(index);
  const justify =
    align === "center" ? "center" : align === "right" ? "flex-end" : "flex-start";
  return {
    display: "flex",
    width: "100%",
    justifyContent: justify,
    whiteSpace: "nowrap",
  };
}
</script>

<style scoped>
/* Tema força text-align:left — reforça alinhamento do cabeçalho. */
.r3-datatable :deep(th.r3-align-center) {
  text-align: center !important;
}

.r3-datatable :deep(th.r3-align-right) {
  text-align: right !important;
}

.r3-datatable :deep(th.r3-align-left) {
  text-align: left !important;
}

.r3-datatable :deep(td.r3-align-center) {
  text-align: center !important;
}

.r3-datatable :deep(.r3-datatable-cell) {
  box-sizing: border-box;
}
</style>
